// ══════════════════════════════════════════════════════════════════
//  Queries de Pipelines (TanStack Query) — dado "frio", staleTime alto.
//
//  IDs de pipeline (por workspace/funil/role) eram re-resolvidos por cada
//  aba do Dashboard (getFunnelData, getLossReasonsData, getForecastData),
//  baixando a mesma listinha N vezes (achado #3). Centralizando aqui:
//   - `usePipelineIds`   → para componentes React;
//   - `fetchPipelineIds` → versão não-hook para uso DENTRO de services,
//     compartilhando o MESMO cache (via queryClient.fetchQuery). Assim a
//     resolução acontece 1× por (workspace, funil, role) na janela de
//     staleTime, independentemente de quantas abas a consomem.
// ══════════════════════════════════════════════════════════════════

import { useQuery } from '@tanstack/react-query';
import { queryClient } from '@lib/queryClient';
import { qk } from './keys';
import { listFunnels, listPipelineIds } from '@app';
import type { PipelineRole } from '@app';

// Pipelines mudam raramente (criados/editados em Settings) → 5min.
const STALE = 5 * 60 * 1000;

function pipelineIdsOpts(
  workspaceId: string | undefined,
  funnelId: string | null,
  role: PipelineRole | null,
) {
  return {
    queryKey: qk.pipelineIds(workspaceId ?? '', funnelId, role),
    queryFn: () => listPipelineIds({ workspaceId: workspaceId as string, funnelId, role }),
    staleTime: STALE,
  };
}

/** Hook React: IDs de pipeline ativos do workspace (cacheado). */
export function usePipelineIds(
  workspaceId?: string,
  funnelId: string | null = null,
  role: PipelineRole | null = null,
) {
  return useQuery({ ...pipelineIdsOpts(workspaceId, funnelId, role), enabled: !!workspaceId });
}

export function useFunnels(workspaceId?: string) {
  return useQuery({
    queryKey: qk.funnels(workspaceId ?? ''),
    queryFn: () => listFunnels(workspaceId as string),
    enabled: !!workspaceId,
    staleTime: STALE,
  });
}

/**
 * Versão não-hook para services: resolve via o mesmo cache do queryClient.
 * Retorna o array cacheado se ainda fresco; senão busca e popula o cache.
 */
export function fetchPipelineIds(
  workspaceId: string,
  funnelId: string | null = null,
  role: PipelineRole | null = null,
): Promise<string[]> {
  return queryClient.fetchQuery(pipelineIdsOpts(workspaceId, funnelId, role));
}
